import { type SelectHTMLAttributes } from "react";

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "children"> {
  options: SelectOption[];
  placeholder?: string;
}

/**
 * A caller-supplied width REPLACES the full-width default instead of racing it.
 * Two width utilities on one element are resolved by stylesheet order, not by
 * the order they appear in the attribute, so `w-auto` — the one value that lets
 * a native select size itself to its longest option — could not reliably beat
 * the base `w-full`, and controls in narrow columns clipped their own options
 * ("days before due" rendered as "days").
 */
const WIDTH_UTILITY = /(?:^|\s)(?:w-|min-w-|max-w-|basis-|flex-)/;

export function Select({ options, placeholder, className = "", ...props }: SelectProps) {
  const width = WIDTH_UTILITY.test(className) ? "" : "w-full";
  return (
    <select
      className={`${width} rounded-md border border-gray-300 bg-white px-3 py-2 text-sm max-sm:text-base max-sm:min-h-11 dark:border-gray-600 dark:bg-gray-700 dark:text-white ${className}`}
      {...props}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}
